/**
 * TODO 7.1 — Step4Services.jsx
 * 
 * Tela 4 — Serviços Oferecidos
 * Grid de chips toggle com sugestões + possibilidade de adicionar serviços livres
 * 
 * Props:
 *   wizard = useWelcomeWizard()
 * 
 * Estado guardado em wizard.state.services = [{ id, name }, ...]
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  TextField,
  Grid2 as Grid,
  Stack,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { getWelcomeText, getWelcomeList } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';

export default function Step4Services({ wizard, goNext }) {
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set());
  const [customServiceInput, setCustomServiceInput] = useState('');
  const [customServices, setCustomServices] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  const { template, state } = wizard;
  const isTeam = state.professionals.length > 1;

  // Inicializa estado a partir do wizard.state
  useEffect(() => {
    if (state.services && state.services.length > 0) {
      const existingIds = new Set();
      state.services.forEach((svc) => {
        if (!customServices.find((cs) => cs.id === svc.id)) {
          existingIds.add(svc.id);
        }
      });
      setSelectedServiceIds(existingIds);

      // Reconstrói custom services
      const customSvcs = state.services.filter(
        (svc) => !svc.id.toString().startsWith('suggestion-')
      );
      setCustomServices(customSvcs);
    }
  }, []);

  // Títulos temáticos
  const [titleText, setTitleText] = useState('');
  const [hintText, setHintText] = useState('');

  useEffect(() => {
    const loadText = async () => {
      const title = await getWelcomeText(template, isTeam ? 'servicesTitle' : 'servicesSingleTitle');
      const hint = await getWelcomeText(template, 'servicesHint');
      setTitleText(title);
      setHintText(hint);
    };
    loadText();
  }, [template, isTeam]);

  // Toggle seleção de serviço
  const handleToggleService = (id) => {
    setSelectedServiceIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  // Adiciona serviço customizado
  const handleAddCustomService = () => {
    const trimmed = customServiceInput.trim();
    if (!trimmed) return;

    const newId = Date.now(); // ID único temporário
    const newService = { id: newId, name: trimmed };
    setCustomServices([...customServices, newService]);
    setSelectedServiceIds((prev) => new Set([...prev, newId]));
    setCustomServiceInput('');
    setShowAddForm(false);
  };

  // Remove serviço customizado
  const handleRemoveCustomService = (id) => {
    setCustomServices((prev) => prev.filter((svc) => svc.id !== id));
    setSelectedServiceIds((prev) => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
  };

  // Sugestões temáticas — precisa ser declarado ANTES de finalServices (TDZ).
  // getWelcomeList é síncrono — pode inicializar direto, sem efeito.
  const [loadedSuggestions, setLoadedSuggestions] = useState(
    () => getWelcomeList(template, 'serviceSuggestions') || []
  );

  useEffect(() => {
    const list = getWelcomeList(template, 'serviceSuggestions') || [];
    wlog('Step4Services', 'sugestões (re)carregadas', list);
    setLoadedSuggestions(list);
  }, [template]);

  // Monta a lista final de serviços = sugestões selecionadas + customizados.
  const finalServices = useMemo(() => {
    const result = [];

    // Sugestões selecionadas — id no formato "suggestion-N"
    loadedSuggestions.forEach((suggestion, idx) => {
      if (selectedServiceIds.has(`suggestion-${idx}`)) {
        result.push({ id: result.length, name: suggestion.name });
      }
    });

    // Serviços customizados selecionados
    customServices.forEach((svc) => {
      if (selectedServiceIds.has(svc.id)) {
        result.push({ id: result.length, name: svc.name });
      }
    });

    wlog('Step4Services', 'finalServices recalculado', {
      selecionados: [...selectedServiceIds],
      finalServices: result,
    });
    return result;
  }, [selectedServiceIds, customServices, loadedSuggestions]);

  // Continuar
  const handleContinue = () => {
    wlog('Step4Services', 'handleContinue()', {
      finalServices,
      qtd: finalServices.length,
    });
    if (finalServices.length === 0) return;

    wizard.setServices(finalServices);
    (goNext || wizard.goNext)();
  };

  const colors = wizard.template?.colors || {};

  wlog('Step4Services', 'RENDER', {
    templateId: template?.id || 'NENHUM',
    loadedSuggestions: loadedSuggestions.length,
    selecionados: [...selectedServiceIds],
    customServices: customServices.length,
  });

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        backgroundColor: colors.backgroundPaper || colors.background || '#f9f9f9',
      }}
    >
      <Container maxWidth="sm">
        <section>
          {/* Título temático */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              marginBottom: '8px',
              textAlign: 'center',
              color: colors.text || '#000',
            }}
          >
            {titleText}
          </Typography>

          {/* Hint temático */}
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary || 'rgba(0,0,0,0.6)',
              marginBottom: '32px',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {hintText}
          </Typography>

          {/* Grid de sugestões */}
          {loadedSuggestions.length > 0 && (
            <Box sx={{ marginBottom: '24px' }}>
              <Grid container spacing={1} sx={{ marginBottom: '16px' }}>
                {loadedSuggestions.map((suggestion, idx) => {
                  const id = `suggestion-${idx}`;
                  const isSelected = selectedServiceIds.has(id);
                  return (
                    <Grid key={id} size={{ xs: 'auto' }}>
                      <Chip
                        label={`${suggestion.emoji} ${suggestion.name}`}
                        onClick={() => handleToggleService(id)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{
                          transition: 'all 0.2s',
                          borderColor: colors.divider || 'rgba(0,0,0,0.12)',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? colors.primary
                              : colors.backgroundPaper,
                          },
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Serviços customizados */}
          {customServices.length > 0 && (
            <Box sx={{ marginBottom: '24px' }}>
              <Grid container spacing={1}>
                {customServices.map((service) => {
                  const isSelected = selectedServiceIds.has(service.id);
                  return (
                    <Grid key={service.id} size={{ xs: 'auto' }}>
                      <Chip
                        label={service.name}
                        onDelete={() => handleRemoveCustomService(service.id)}
                        onClick={() => handleToggleService(service.id)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{
                          transition: 'all 0.2s',
                          borderColor: colors.divider || 'rgba(0,0,0,0.12)',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? colors.primary
                              : colors.backgroundPaper,
                          },
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Adicionar serviço */}
          {!showAddForm && (
            <Box sx={{ marginBottom: '32px', textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  color: colors.textSecondary || 'rgba(0,0,0,0.6)',
                  borderColor: colors.divider || 'rgba(0,0,0,0.12)',
                  '&:hover': {
                    borderColor: colors.primary || '#1976d2',
                    // Fundo primário translúcido (hex 8 dígitos: '0A' ≈ 4% de opacidade).
                    backgroundColor: `${colors.primary || '#1976d2'}0A`,
                  },
                }}
              >
                + Adicionar outro
              </Button>
            </Box>
          )}

          {showAddForm && (
            <Box sx={{ marginBottom: '32px' }}>
              <TextField
                fullWidth
                placeholder="Nome do serviço"
                value={customServiceInput}
                onChange={(e) => setCustomServiceInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCustomService();
                  }
                }}
                size="small"
                variant="outlined"
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={handleAddCustomService}
                        disabled={!customServiceInput.trim()}
                        sx={{ textTransform: 'none', marginRight: '8px' }}
                      >
                        OK
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setShowAddForm(false);
                          setCustomServiceInput('');
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancelar
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* Botão Continuar */}
          <Stack direction="column" spacing={1} sx={{ alignItems: 'center' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinue}
                disabled={selectedServiceIds.size === 0}
                sx={{
                  textTransform: 'none',
                  minWidth: '120px',
                  padding: '10px 24px',
                }}
              >
                Continuar
              </Button>
            </Stack>
            {selectedServiceIds.size === 0 && (
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary || 'rgba(0,0,0,0.5)', textAlign: 'center' }}
              >
                Selecione pelo menos um serviço para continuar
              </Typography>
            )}
          </Stack>
        </section>
      </Container>
    </Box>
  );
}
